/**
 * The leaf decision for the Word add-in, as data.
 *
 * The mirror of `asthra-v4-web/src/new-document/leaf-step.ts` — deliberately
 * the same three questions, answered by the SAME shared modules, because the
 * two surfaces must not disagree about which document a section files into.
 * Only the surrounding UI differs: the web asks inside a four-step wizard, the
 * add-in inside its doctype/section pickers.
 *
 * Nothing about the partition is re-derived here. It is resolved server-side by
 * `csr_sim.doc_types.documents.effective_documents` and arrives on
 * `documents` from `GET /doc-types/{code}`.
 */

import { buildDocumentRows, leafChoices } from "@shared/client/documentPicker";
import type { DocumentPickerCatalog } from "@shared/client/documentPicker";
import { scopeCatalogToLeaf } from "@shared/client/leafScope";
import type { OutlineCatalog } from "@shared/client/leafScope";

export interface LeafOption {
  id: string;
  title: string;
  /** How many modules this leaf collects — the writer's sense of its size. */
  moduleCount: number;
}

/**
 * The leaves this doctype offers, or `null` when it is filed as ONE document.
 *
 * `null` is the load-bearing answer: it is what keeps the add-in's pickers
 * rendering exactly as they do today for CSR, PROTOCOL_DRUG, PSUR and every
 * other single-document doctype.
 */
export function leafOptions(catalog: DocumentPickerCatalog | null | undefined): LeafOption[] | null {
  if (!catalog) return null;
  const choices = leafChoices(catalog);
  if (choices === null) return null;
  const counts = new Map((buildDocumentRows(catalog, () => false) ?? []).map((r) => [r.id, r.total]));
  return choices.map((c) => ({ id: c.id, title: c.title, moduleCount: counts.get(c.id) ?? 0 }));
}

/**
 * The catalog the section picker and the outline should show, narrowed to the
 * leaf this document IS.
 *
 * Returns the catalog UNCHANGED — by reference — for a doctype with no leaves,
 * for a document carrying no leaf id (every one created before the split), and
 * for a stale id the doctype no longer declares. That last case must widen
 * back rather than empty the outline: an empty outline reads as "this document
 * has no sections", which a writer cannot tell from a bug.
 */
export function scopedCatalog<C extends OutlineCatalog>(
  catalog: C | null | undefined,
  leafDocumentId: string | null | undefined,
): C | null {
  return scopeCatalogToLeaf(catalog, leafDocumentId);
}

/**
 * The selection, filtered to the leaf the writer just moved to.
 *
 * Ticks belonging to the previous leaf would be sent to the run and drafted
 * into the wrong document.
 */
export function selectionForLeaf(
  catalog: OutlineCatalog | null | undefined,
  leafDocumentId: string | null,
  selected: readonly string[],
): string[] {
  const scoped = scopedCatalog(catalog, leafDocumentId);
  if (!scoped || scoped === catalog) return [...selected];
  const mine = new Set(Object.keys(scoped.modules ?? {}));
  return selected.filter((id) => mine.has(id));
}

/**
 * The session patch that records a document's leaf identity.
 *
 * `leaf_parent_id` is omitted rather than sent as null when no submission was
 * named — the server seeds one, and a null would store an absent parent beside
 * a present leaf, i.e. a leaf that can never find its siblings.
 */
export function leafIdentityPatch(
  leafDocumentId: string,
  leafParentId: string | null,
): { leaf_document_id: string; leaf_parent_id?: string } {
  return leafParentId === null
    ? { leaf_document_id: leafDocumentId }
    : { leaf_document_id: leafDocumentId, leaf_parent_id: leafParentId };
}
